import { dropSpeed, spawnInterval, maxParallel, pickMugType, scoreForCatch } from './spawner.js';
import { tuning } from './tuning.js';

const WORLD = { w: 360, h: 720 };

// Match prototype dimensions for visual consistency.
const BARTENDER = { w: 76, h: 30 };
const MUG = { w: 32, h: 42 };
const BARTENDER_Y = 600; // H - 120

export class GameEngine {
  constructor({ milestones, hooks }) {
    const tuningMs = Array.isArray(tuning.milestones) && tuning.milestones.length > 0 ? tuning.milestones : null;
    this.milestones = (milestones && milestones.length > 0)
      ? milestones
      : (tuningMs || [0, 5, 15, 30, 50]);
    this.hooks = hooks || {};
    this.world = { ...WORLD };
    this.reset();
  }

  reset() {
    this.running = false;
    this.score = 0;
    this.combo = 0;
    this.bestCombo = 0;
    this.lives = tuning.lives || 3;
    this.partyMeter = 0;       // total mugs caught (drives milestones)
    this.stageIdx = 0;          // 0..4
    this.unlocked = new Set([0]); // milestone 0 always unlocked at start
    this.fever = false;
    this.feverUntil = 0;
    this.mugs = [];
    this.lastSpawnAt = 0;
    this.bartender = {
      x: this.world.w / 2 - BARTENDER.w / 2,
      y: BARTENDER_Y,
      w: BARTENDER.w,
      h: BARTENDER.h
    };
  }

  // restoreUnlocks — sau khi reset(), pre-populate Set unlocked + stageIdx khớp với session DB.
  // Gọi khi resume từ pending_respawn HOẶC resume từ active đã unlock vài slot.
  //
  //   unlockedMilestones = sorted array of milestone thresholds đã unlock (excl 0/cover).
  //   e.g. [5, 15] → user đã pass milestone 5 và 15 → stageIdx = 2.
  //
  // partyMeter giữ 0 (per spec: score absolute trên respawn). Khi user catch lại tới mug=5 thì
  // checkMilestone() skip vì unlocked.has(5)=true. Continue tới 30 (next unreached) → fire.
  restoreUnlocks(unlockedMilestones) {
    if (!Array.isArray(unlockedMilestones)) return;
    for (const m of unlockedMilestones) this.unlocked.add(m);
    this.stageIdx = Math.min(this.milestones.length - 1, unlockedMilestones.length);
  }

  start() {
    this.running = true;
  }

  stop() {
    this.running = false;
  }

  stageName() {
    const names = Array.isArray(tuning.stage_names) && tuning.stage_names.length > 0
      ? tuning.stage_names
      : ['Stage'];
    return names[this.stageIdx % names.length];
  }

  // x in logical world coords; clamp inside.
  moveBartender(x) {
    const half = this.bartender.w / 2;
    this.bartender.x = Math.max(0, Math.min(this.world.w - this.bartender.w, x - half));
  }

  // dt in seconds, now in ms (performance.now()).
  update(dt, now) {
    if (!this.running) return;

    // Spawn
    if (now - this.lastSpawnAt > spawnInterval(this.stageIdx) && this.mugs.length < maxParallel(this.stageIdx)) {
      const kind = pickMugType({ fever: this.fever });
      this.mugs.push({
        x: 10 + Math.random() * (this.world.w - MUG.w - 20),
        y: -MUG.h,
        w: MUG.w,
        h: MUG.h,
        vy: dropSpeed(this.stageIdx),
        kind
      });
      this.lastSpawnAt = now;
    }

    // Step + collide
    const bt = this.bartender;
    for (let i = this.mugs.length - 1; i >= 0; i--) {
      const m = this.mugs[i];
      m.y += m.vy * dt;

      const hit = aabb(m, bt);
      const offscreen = m.y > this.world.h + m.h;

      if (hit) {
        this.mugs.splice(i, 1);
        if (m.kind === 'bomb') {
          this.combo = 0;
          if (!this.fever) {
            this.lives--;
            this.hooks.onMiss?.({ kind: 'bomb' });
          } else {
            this.hooks.onCatch?.({ kind: 'bomb-fever', points: 0, x: bt.x + bt.w / 2, y: bt.y });
          }
        } else {
          this.combo++;
          this.bestCombo = Math.max(this.bestCombo, this.combo);
          const points = scoreForCatch({ kind: m.kind, combo: this.combo, fever: this.fever });
          this.score += points;
          this.partyMeter++;
          this.hooks.onCatch?.({ kind: m.kind, points, x: bt.x + bt.w / 2, y: bt.y, combo: this.combo });
          this.checkMilestone();
        }
      } else if (offscreen) {
        this.mugs.splice(i, 1);
        if (m.kind !== 'bomb') {
          this.combo = 0;
          if (!this.fever) {
            this.lives--;
            this.hooks.onMiss?.({ kind: 'mug' });
          }
        }
      }
    }

    // Fever timer
    if (this.fever && now >= this.feverUntil) {
      this.fever = false;
      this.hooks.onFeverEnd?.();
    }

    // Game over
    if (this.lives <= 0) {
      this.running = false;
      this.hooks.onGameOver?.({
        score: this.score,
        bestCombo: this.bestCombo,
        mugsCaught: this.partyMeter
      });
    }
  }

  checkMilestone() {
    const m = this.milestones.find((v) => v === this.partyMeter && !this.unlocked.has(v));
    if (m == null) return;
    this.unlocked.add(m);
    this.stageIdx = Math.min(this.milestones.length - 1, this.stageIdx + 1);
    this.fever = true;
    this.feverUntil = performance.now() + (tuning.fever_duration_ms || 4500);
    this.hooks.onMilestone?.({ milestone: m, stageIdx: this.stageIdx, stageName: this.stageName() });
  }
}

function aabb(a, b) {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}
